import React, { useState } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Mail, Lock, Eye, EyeOff, KeyRound, Loader2, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import WorldMapBackground from "@/components/WorldMapBackground";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, ready, login } = useAuth();

  const searchParams = new URLSearchParams(location.search);
  const planParam = searchParams.get("plan");
  const packParam = searchParams.get("pack");
  const redirectParam = searchParams.get("redirect");

  // "login" | "forgot_email" | "forgot_reset"
  const [view, setView] = useState("login");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Forgot Password state
  const [resetEmail, setResetEmail] = useState("");
  const [resetOtp, setResetOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState("");

  if (ready && user) {
    if ((planParam && planParam !== "Free") || packParam) {
      const targetBase = user.role === "recruiter" ? "/recruiter/pricing" : "/app/pricing";
      return <Navigate to={`${targetBase}${location.search}`} replace />;
    }
    return <Navigate to={user.role === "recruiter" ? "/recruiter" : "/app"} replace />;
  }

  const go = (u) => navigate(u.role === "recruiter" ? "/recruiter" : "/app");

  const handleLoginSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}`);

      // If user came with a plan/pack intent, take them to our inside portal get credit / plan page
      if ((planParam && planParam !== "Free") || packParam) {
        const targetBase = res.user.role === "recruiter" ? "/recruiter/pricing" : "/app/pricing";
        navigate(`${targetBase}${location.search}`);
        return;
      }

      if (redirectParam) {
        navigate(redirectParam);
        return;
      }
      go(res.user);
    } else {
      setError(res.error);
    }
  };

  const handleSendResetCode = async (e) => {
    e?.preventDefault();
    const cleanEmail = (resetEmail || email).trim();
    if (!cleanEmail) {
      setResetError("Please enter your email address.");
      return;
    }
    setResetLoading(true);
    setResetError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email: cleanEmail });
      toast.success(data.message || "Reset code sent to your email.");
      setResetEmail(cleanEmail);
      setView("forgot_reset");
    } catch (err) {
      setResetError(formatApiError(err?.response?.data?.detail) || "Could not send reset code. Please check your email address.");
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e?.preventDefault();
    if (!resetOtp.trim()) {
      setResetError("Please enter the 6-digit verification code.");
      return;
    }
    if (newPassword.length < 6) {
      setResetError("New password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetError("Passwords do not match. Please re-enter.");
      return;
    }

    setResetLoading(true);
    setResetError("");
    try {
      const { data } = await api.post("/auth/reset-password", {
        email: resetEmail,
        otp: resetOtp.trim(),
        new_password: newPassword,
      });

      if (data.token) {
        localStorage.setItem("cvolve_token", data.token);
      }
      toast.success(data.message || "Password reset successfully!");
      if (data.user) {
        go(data.user);
      } else {
        setEmail(resetEmail);
        setPassword(newPassword);
        setView("login");
      }
    } catch (err) {
      setResetError(formatApiError(err?.response?.data?.detail) || "Could not reset password. Please verify the code.");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-5 py-10">
      <WorldMapBackground />
      <div className="absolute left-5 top-5"><Logo /></div>
      <div className="absolute right-5 top-5"><ThemeToggle /></div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="card-surface p-8 shadow-float">
          <AnimatePresence mode="wait">
            {/* 1. STANDARD LOGIN VIEW */}
            {view === "login" && (
              <motion.div
                key="login-view"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
              >
                {(planParam || packParam) && (
                  <div className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-electric/30 bg-electric/10 px-3 py-1 text-xs font-semibold text-electric">
                    <Sparkles className="h-3.5 w-3.5" /> Selected: {planParam || packParam}
                  </div>
                )}
                <h1 className="font-heading text-3xl font-extrabold tracking-tight">Welcome back</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  {(planParam || packParam) ? "Log in to proceed directly to payment." : "Log in to your Career Profile."}
                </p>

                <form onSubmit={handleLoginSubmit} className="mt-6 space-y-4">
                  <div>
                    <label className="label-caps">Email</label>
                    <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-border bg-card/60 focus-within:border-electric focus-within:ring-2 focus-within:ring-electric/20 px-3 transition-all">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <input
                        data-testid="login-email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full bg-transparent py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <label className="label-caps">Password</label>
                      <button
                        type="button"
                        onClick={() => {
                          setResetEmail(email);
                          setResetError("");
                          setView("forgot_email");
                        }}
                        className="text-xs font-semibold text-electric hover:underline"
                        data-testid="login-forgot-btn"
                      >
                        Forgot?
                      </button>
                    </div>
                    <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-border bg-card/60 focus-within:border-electric focus-within:ring-2 focus-within:ring-electric/20 px-3 transition-all">
                      <Lock className="h-4 w-4 text-muted-foreground" />
                      <input
                        data-testid="login-password"
                        type={show ? "text" : "password"}
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full bg-transparent py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setShow(!show)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <p data-testid="login-error" className="text-xs font-medium text-destructive">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="login-submit"
                    className="cv-btn-primary w-full py-3 flex items-center justify-center gap-2 shadow-glow"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
                      </>
                    ) : (
                      <>
                        {(planParam || packParam) ? `Log In & Continue to Plans` : `Log In`} <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </form>

                <p className="mt-6 text-center text-sm text-muted-foreground">
                  New to CVOLVE?{" "}
                  <Link to={`/signup${location.search}`} className="font-semibold text-electric hover:underline">
                    Start free
                  </Link>
                </p>
              </motion.div>
            )}

            {/* 2. FORGOT PASSWORD - STEP 1: ENTER EMAIL */}
            {view === "forgot_email" && (
              <motion.div
                key="forgot-email-view"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <button
                    type="button"
                    onClick={() => {
                      setError("");
                      setView("login");
                    }}
                    className="rounded-full p-1 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <span className="label-caps text-electric">Account Recovery</span>
                </div>
                <h1 className="font-heading text-2xl font-extrabold tracking-tight">Reset your password</h1>
                <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
                  Enter your email address and we'll send you a 6-digit verification code to reset your password.
                </p>

                <form onSubmit={handleSendResetCode} className="mt-6 space-y-4">
                  <div>
                    <label className="label-caps">Email address</label>
                    <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-border bg-card/60 focus-within:border-electric focus-within:ring-2 focus-within:ring-electric/20 px-3 transition-all">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <input
                        data-testid="forgot-email-input"
                        type="email"
                        autoComplete="email"
                        required
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full bg-transparent py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none"
                      />
                    </div>
                  </div>

                  {resetError && (
                    <p className="text-xs font-medium text-destructive leading-relaxed">
                      {resetError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={resetLoading}
                    data-testid="forgot-send-code-btn"
                    className="cv-btn-primary w-full py-3 flex items-center justify-center gap-2 shadow-glow"
                  >
                    {resetLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Sending code…
                      </>
                    ) : (
                      <>
                        Send 6-Digit Code <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => setView("login")}
                    className="text-xs font-semibold text-muted-foreground hover:text-foreground"
                  >
                    ← Back to Log In
                  </button>
                </div>
              </motion.div>
            )}

            {/* 3. FORGOT PASSWORD - STEP 2: ENTER OTP & NEW PASSWORD */}
            {view === "forgot_reset" && (
              <motion.div
                key="forgot-reset-view"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <button
                    type="button"
                    onClick={() => setView("forgot_email")}
                    className="rounded-full p-1 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <span className="label-caps text-electric">Set New Password</span>
                </div>
                <h1 className="font-heading text-2xl font-extrabold tracking-tight">Verify & Reset</h1>
                <p className="mt-1 text-xs text-muted-foreground">
                  Code sent to <span className="font-semibold text-foreground">{resetEmail}</span>
                </p>

                <form onSubmit={handleResetPassword} className="mt-5 space-y-4">
                  <div>
                    <label className="label-caps">6-Digit Verification Code</label>
                    <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-border bg-card/60 focus-within:border-electric focus-within:ring-2 focus-within:ring-electric/20 px-3 transition-all">
                      <KeyRound className="h-4 w-4 text-muted-foreground" />
                      <input
                        data-testid="reset-otp-input"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        required
                        value={resetOtp}
                        onChange={(e) => setResetOtp(e.target.value.replace(/\s+/g, ""))}
                        placeholder="123456"
                        className="w-full bg-transparent py-2.5 text-sm tracking-widest font-mono text-foreground placeholder:text-muted-foreground outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="label-caps">New Password</label>
                    <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-border bg-card/60 focus-within:border-electric focus-within:ring-2 focus-within:ring-electric/20 px-3 transition-all">
                      <Lock className="h-4 w-4 text-muted-foreground" />
                      <input
                        data-testid="reset-new-password"
                        type={showNewPwd ? "text" : "password"}
                        autoComplete="new-password"
                        required
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="At least 6 characters"
                        className="w-full bg-transparent py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPwd(!showNewPwd)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        {showNewPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="label-caps">Confirm New Password</label>
                    <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-border bg-card/60 focus-within:border-electric focus-within:ring-2 focus-within:ring-electric/20 px-3 transition-all">
                      <Lock className="h-4 w-4 text-muted-foreground" />
                      <input
                        data-testid="reset-confirm-password"
                        type={showNewPwd ? "text" : "password"}
                        autoComplete="new-password"
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-type new password"
                        className="w-full bg-transparent py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none"
                      />
                    </div>
                  </div>

                  {resetError && (
                    <p className="text-xs font-medium text-destructive leading-relaxed">
                      {resetError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={resetLoading}
                    data-testid="reset-submit-btn"
                    className="cv-btn-primary w-full py-3 flex items-center justify-center gap-2 shadow-glow"
                  >
                    {resetLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Updating password…
                      </>
                    ) : (
                      <>
                        Reset Password & Sign In <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-5 flex items-center justify-between text-xs">
                  <button
                    type="button"
                    onClick={handleSendResetCode}
                    className="font-semibold text-electric hover:underline"
                  >
                    Resend Code
                  </button>
                  <button
                    type="button"
                    onClick={() => setView("login")}
                    className="font-semibold text-muted-foreground hover:text-foreground"
                  >
                    Back to Log In
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
